// ============================================================================
// IAMMUZIK AI FEATURE POLICY — PERMANENT PLATFORM RULE
// ============================================================================
//
// WHAT AI DOES ON IAMMUZIK (the real role):
//   AI helps with PRODUCTION and VISUALS only. It builds the beat, can perform
//   the vocal, and makes the video — so a creator who can't sing or shoot
//   video can still bring a song to life.
//
// WHAT AI NEVER DOES — THE ONE LINE THAT NEVER MOVES:
//   AI NEVER WRITES LYRICS. Not a line, a rhyme, a hook, a verse, a chorus, a
//   title, a rewrite, a completion, or a "suggestion." Lyrics are 100% human.
//   The creator either uploads a recording of themselves reciting the lyrics,
//   or types the lyrics by hand. AI can perform those user-written lyrics with
//   a synthetic voice — but it never authors them.
//
// WHY THIS SETS IAMMUZIK APART:
//   Other sites let AI write the song. Here the song is always the creator's.
//   "AI builds the beat and the video. The lyrics and the songwriting are
//   always you."
// ============================================================================

export const AI_FEATURE_POLICY = {
  /** AI NEVER generates, suggests, rewrites, completes, or alters lyrics. */
  lyricGenerationAllowed: false,
  lyricSuggestionsAllowed: false,
  lyricRewriteAllowed: false,
  lyricCompletionAllowed: false,
  rhymeGenerationAllowed: false,
  hookGenerationAllowed: false,
  verseGenerationAllowed: false,
  chorusGenerationAllowed: false,
  songwritingAssistAllowed: false,

  /**
   * AI CAN perform the creator's OWN written lyrics using a synthetic voice.
   * This is allowed because not everyone can sing, but everyone can write a
   * helluva song. The lyrics are still 100% the creator's — AI only voices them.
   */
  aiVocalPerformanceAllowed: true,

  /** AI can generate royalty-free instrumentals / beats (no vocals, no lyrics). */
  instrumentalGenerationAllowed: true,

  /** AI can generate music videos from a finished track. */
  musicVideoGenerationAllowed: true,
  /** AI can generate a short (10–15s) promo video demo/trailer. */
  videoDemoTrailerAllowed: true,

  /** AI may NEVER clone or impersonate a REAL person's voice. */
  realPersonVoiceCloneAllowed: false,
  vocalImpersonationAllowed: false,
} as const;

/**
 * What AI is actually FOR on IAMMUZIK, in plain language for the site.
 */
export const AI_ROLE_EXPLANATION: string =
  'On IAMMUZIK, AI helps with production and visuals only. ' +
  'It can generate royalty-free instrumentals (beats), perform your written lyrics with a synthetic voice if you choose, ' +
  'and create music videos and short promo trailers. ' +
  'What AI never does is write your lyrics — every word is yours. ' +
  'You either upload a recording of yourself reciting your lyrics, or you type them yourself. ' +
  'AI builds the beat and the video; the lyrics and the songwriting are always you.';

export const AI_ONE_LINER: string =
  'AI builds the beat and the video. The lyrics and the songwriting are always you.';

/**
 * The ONLY AI features IAMMUZIK offers. Notice what is NOT here: any lyric,
 * rhyme, verse, hook, chorus, songwriting, or text-generation tool, and any
 * real-person voice cloning or impersonation.
 */
export type AllowedAiFeature =
  | 'INSTRUMENTAL_BEAT_GENERATION'
  | 'AI_VOCAL_PERFORMANCE_OF_USER_LYRICS'
  | 'MUSIC_VIDEO_GENERATION'
  | 'PROMO_VIDEO_DEMO_TRAILER'
  | 'AUDIO_FILE_ANALYSIS'
  | 'LOUDNESS_METERING'
  | 'NOISE_DETECTION'
  | 'AUDIO_METADATA_TAGGING';

/**
 * Which contracts unlock which AI features.
 */
export const AI_FEATURE_BY_TIER: Record<
  'FREE' | 'TIER_1' | 'TIER_2' | 'TIER_3' | 'TIER_4',
  AllowedAiFeature[]
> = {
  FREE: [],
  TIER_1: [],
  TIER_2: [],
  TIER_3: [
    'INSTRUMENTAL_BEAT_GENERATION',
    'AI_VOCAL_PERFORMANCE_OF_USER_LYRICS',
    'PROMO_VIDEO_DEMO_TRAILER',
  ],
  TIER_4: [
    'INSTRUMENTAL_BEAT_GENERATION',
    'AI_VOCAL_PERFORMANCE_OF_USER_LYRICS',
    'MUSIC_VIDEO_GENERATION',
    'PROMO_VIDEO_DEMO_TRAILER',
  ],
};

/**
 * Guard used anywhere lyrics are entered or stored. The platform has no
 * lyric-generation pathway at all — this documents that intent.
 */
export function assertLyricsAreAuthentic(context: string): void {
  if (!AI_FEATURE_POLICY.lyricGenerationAllowed) {
    return;
  }
  throw new Error(
    `[IAMMUZIK POLICY VIOLATION] ${context}: AI lyric generation is permanently disabled.`,
  );
}
